import { useMemo } from 'react';
import { EntradaSeleccionada } from '@/schemas/checkout.schema';
import { StockDisponibleResponse } from '@/services/backend/new.funciones.service';

export interface StockConflict {
    tipoEntradaId: string;
    nombre: string;
    seleccionadas: number;
    disponible: number;
    exceso: number; // how many need to be removed
}

export interface StockConflictResult {
    hasConflict: boolean;
    conflicts: StockConflict[];
    totalExceso: number;
}

/**
 * Hook para detectar conflictos de stock en eventos sin mapa.
 * Compara las entradas seleccionadas contra el stock disponible actual.
 * 
 * @param entradas - Entradas seleccionadas por el usuario
 * @param stockReservado - Stock disponible actual del servidor
 * @returns Información sobre conflictos de stock detectados
 */
export function useStockConflict(
    entradas: EntradaSeleccionada[],
    stockReservado: StockDisponibleResponse['entradas']
): StockConflictResult {
    return useMemo(() => {
        if (!stockReservado || stockReservado.length === 0) {
            return {
                hasConflict: false,
                conflicts: [],
                totalExceso: 0
            };
        }

        const conflicts: StockConflict[] = [];

        // Verificar cada tipo de entrada seleccionada
        entradas.forEach((entrada) => {
            const stockInfo = stockReservado.find((s: StockDisponibleResponse['entradas'][0]) => s.tipoEntradaId === entrada.tipoEntradaId);
            const seleccionadas = entrada.asientos?.length || 0;
            const disponible = stockInfo?.disponible || 0;

            // Si hay más seleccionadas que disponibles, hay conflicto
            if (seleccionadas > disponible) {
                conflicts.push({
                    tipoEntradaId: entrada.tipoEntradaId,
                    nombre: entrada.nombre,
                    seleccionadas,
                    disponible,
                    exceso: seleccionadas - disponible
                });
            }
        });

        const totalExceso = conflicts.reduce((sum, c) => sum + c.exceso, 0);

        return {
            hasConflict: conflicts.length > 0,
            conflicts,
            totalExceso
        };
    }, [entradas, stockReservado]);
}
